import type { CSSProperties } from "react"
import {
  Feather,
  Globe,
  Scale,
  Target,
  type LucideIcon,
} from "lucide-react"
import { Separator } from "@workspace/ui/components/separator"
import { useI18n } from "@/lib/i18n"

const ICONS: Record<string, LucideIcon> = {
  target: Target,
  globe: Globe,
  scale: Scale,
  feather: Feather,
}

export function Principles() {
  const { t } = useI18n()

  return (
    <section
      id="principles"
      className="relative border-t border-border py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-14 xl:px-20">
        {/* Numbered heading */}
        <header className="flex items-center gap-5">
          <h2 className="flex items-baseline gap-3 text-2xl font-semibold tracking-[-0.02em] md:text-3xl">
            <span className="font-mono text-sm font-normal text-primary">04.</span>
            <span className="text-foreground">{t.principles.eyebrow}</span>
          </h2>
          <Separator className="h-px flex-1 bg-foreground/20" />
        </header>

        {/* Intro */}
        <div className="mt-12 grid grid-cols-12">
          <h3 className="col-span-12 text-[clamp(1.9rem,4.2vw,3.3rem)] leading-[1.05] font-extrabold tracking-[-0.03em] text-foreground md:col-span-8">
            {t.principles.title}
          </h3>
        </div>

        {/* Grid of principles */}
        <ol className="mt-16 grid grid-cols-1 gap-x-10 gap-y-14 md:grid-cols-2 lg:gap-x-16">
          {t.principles.items.map((item, i) => {
            const Icon = ICONS[item.icon] ?? Target
            return (
              <li
                key={item.n}
                className="group relative pl-0"
                style={{ "--d": i + 1 } as CSSProperties}
              >
                <div className="flex items-start gap-5">
                  {/* Icon + number stack */}
                  <div className="flex shrink-0 flex-col items-center gap-2 pt-0.5">
                    <span
                      aria-hidden
                      className="inline-flex size-9 items-center justify-center border border-border text-primary transition-colors duration-300 group-hover:border-primary/60 group-hover:bg-primary/5"
                    >
                      <Icon className="size-4" strokeWidth={1.6} />
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.14em] text-primary">
                      {item.n}.
                    </span>
                  </div>
                  <div>
                    <h4 className="text-[1.4rem] leading-[1.2] font-bold tracking-[-0.02em] text-foreground">
                      {item.t}
                    </h4>
                    <p className="mt-3 max-w-lg text-[15px] leading-[1.7] text-foreground/65">
                      {item.d}
                    </p>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
